import { useState } from 'react';
import { useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/currencyUtil';
import { describePosCustomization } from '../utils/posCustomizationUtil';
import { POS_CUSTOMIZATIONS_MAX, POS_EXTRAS_MAX } from '../constants/posConst';
import PosSortableRows from './PosSortableRows';
import PosCustomizationModal from './PosCustomizationModal';
import PosExtraModal from './PosExtraModal';
import '../css/Pos.css';

/**
 * The "With customization options" block of the product form: the checkbox,
 * then the product's OPTIONS (questions like sugar level) and its EXTRAS
 * (paid add-ons like an extra pearl), each with its own Add button.
 *
 * Holds no data of its own — both lists belong to the product form, which
 * saves them with the rest of the product. Unticking hides them without
 * clearing them, so a tick taken back by mistake does not throw away a
 * carefully typed set of choices; the product form decides what unticked
 * means on save.
 *
 * @param {object} props
 * @param {boolean} props.enabled - the checkbox
 * @param {(enabled: boolean) => void} props.onEnabledChange
 * @param {Array<object>} props.options - the product's options, in order
 * @param {(options: Array<object>) => void} props.onChange - the whole new options list
 * @param {Array<object>} props.extras - the product's extras, in order
 * @param {(extras: Array<object>) => void} props.onExtrasChange - the whole new extras list
 * @param {boolean} [props.disabled] - while the product form is saving
 * @returns {JSX.Element}
 */
function PosCustomizationField({
  enabled, onEnabledChange, options, onChange, extras, onExtrasChange, disabled = false,
}) {
  const { config } = useConfig();
  // For each dialog — undefined: closed. null: adding. An object: editing it.
  const [editingOption, setEditingOption] = useState(undefined);
  const [editingExtra, setEditingExtra] = useState(undefined);
  const optionsFull = options.length >= POS_CUSTOMIZATIONS_MAX;
  const extrasFull = extras.length >= POS_EXTRAS_MAX;

  /**
   * Puts a saved record into its list — replacing it in place when edited, so
   * the order does not shuffle on every edit.
   *
   * @param {Array<object>} list - the current list
   * @param {object} saved - the cleaned record from a dialog
   * @returns {Array<object>} the new list
   */
  const upsert = (list, saved) => (
    list.some((item) => item.id === saved.id)
      ? list.map((item) => (item.id === saved.id ? saved : item))
      : [...list, saved]
  );

  return (
    <div className="pos-opt-block">
      <label className="pos-check">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onEnabledChange(e.target.checked)}
          disabled={disabled}
        />
        <span>
          <strong>With customization options</strong>
          <em>Ask for things like sugar level, or offer paid extras, when this is sold</em>
        </span>
      </label>

      {enabled && (
        <>
          {options.length > 0 && <p className="pos-opt-section">Options</p>}
          <PosSortableRows
            items={options}
            describe={(option) => ({ title: option.label, detail: describePosCustomization(option) })}
            onChange={onChange}
            onEdit={setEditingOption}
            hint="Drag to reorder (press and hold on a touch screen). Staff are asked in this order."
            disabled={disabled}
          />

          {extras.length > 0 && <p className="pos-opt-section">Extras</p>}
          <PosSortableRows
            items={extras}
            describe={(item) => ({
              title: item.label,
              detail: [`+${formatMoney(item.price, config)} each`, item.description].filter(Boolean).join(' · '),
            })}
            onChange={onExtrasChange}
            onEdit={setEditingExtra}
            hint="Drag to reorder. The till lists extras in this order."
            disabled={disabled}
          />

          <div className="pos-opt-add-row">
            <button
              type="button"
              className="pos-stock-add pos-opt-add"
              onClick={() => setEditingOption(null)}
              disabled={disabled || optionsFull}
            >
              Add options
            </button>
            <button
              type="button"
              className="pos-stock-add pos-opt-add"
              onClick={() => setEditingExtra(null)}
              disabled={disabled || extrasFull}
            >
              Add extras
            </button>
          </div>

          {options.length === 0 && extras.length === 0 && (
            <span className="pos-theme-hint">
              Nothing is asked at the till until you add an option or an extra.
            </span>
          )}
          {optionsFull && (
            <span className="pos-theme-hint">That is the most options one product can have ({POS_CUSTOMIZATIONS_MAX}).</span>
          )}
          {extrasFull && (
            <span className="pos-theme-hint">That is the most extras one product can have ({POS_EXTRAS_MAX}).</span>
          )}
        </>
      )}

      {editingOption !== undefined && (
        <PosCustomizationModal
          option={editingOption}
          siblings={options}
          onSave={(saved) => { onChange(upsert(options, saved)); setEditingOption(undefined); }}
          onClose={() => setEditingOption(undefined)}
        />
      )}

      {editingExtra !== undefined && (
        <PosExtraModal
          extra={editingExtra}
          siblings={extras}
          onSave={(saved) => { onExtrasChange(upsert(extras, saved)); setEditingExtra(undefined); }}
          onClose={() => setEditingExtra(undefined)}
        />
      )}
    </div>
  );
}

export default PosCustomizationField;
